import cn from "classnames";

import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { deleteMultiCityCode, setMultiCityCode } from "@/api/multiCityCode";
import { AirportMapItem } from "@/constants/airport";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import { useMultiCityCodes } from "@/hooks/useMultiCityCodes";
import usePlatform from "@/hooks/usePlatform";

import SelectAirport from "../Airport/SelectAirport";

interface AirportCode {
  code: string;
  description: string;
  airports: string[];
  type: string;
}

interface ApiMultiCityCodeForm {
  multicity_code: string;
  description: string;
  airports: AirportMapItem[];
}

interface SupportedCodesProps {
  title?: string;
  buttonText?: string;
  className?: string;
}

// 表格内容组件
const CodesTable: React.FC<{
  codes: AirportCode[];
  showAction?: boolean;
  onDelete?: (code: string) => void;
  deleteLoading?: string | false;
}> = ({ codes, onDelete, deleteLoading, showAction }) => {
  if (codes.length === 0) {
    return null;
  }
  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label="supported codes table">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold", width: "150px" }}>Multi-City Code</TableCell>
            <TableCell sx={{ fontWeight: "bold", width: "200px" }}>Description</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Corresponding Airports</TableCell>
            {showAction && <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {codes
            .sort((a, b) => a.code.localeCompare(b.code))
            .map((row) => (
              <TableRow key={row.code} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                <TableCell component="th" scope="row">
                  {row.code}
                </TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.airports.join(", ")}</TableCell>
                {showAction && (
                  <TableCell align="center">
                    {row.type === "custom" ? (
                      <LoadingButton
                        variant="text"
                        size="small"
                        sx={{
                          minWidth: "auto",
                          p: 0,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          ml: 2,
                        }}
                        loading={deleteLoading === row.code}
                        onClick={() => {
                          onDelete?.(row.code);
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: "18px" }} />
                      </LoadingButton>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// 主组件
const SupportedCodes: React.FC<SupportedCodesProps> = ({
  title = "Multi-City Codes",
  buttonText = "Multi-City Codes",
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [formData, setFormData] = useState<ApiMultiCityCodeForm>({
    multicity_code: "",
    description: "",
    airports: [],
  });
  const [errors, setErrors] = useState({
    multicity_code: false,
    multicity_code_text: "",
    airports: false,
  });

  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string>("");

  const { isMobile } = usePlatform();

  const { staticCodes, customCodes } = useMultiCityCodes();

  const store = useGlobalStoreContext();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAddOpen = () => {
    setOpenAdd(true);
  };

  const handleAddClose = () => {
    setOpenAdd(false);
    setFormData({
      multicity_code: "",
      description: "",
      airports: [],
    });
    setErrors({
      multicity_code: false,
      multicity_code_text: "",
      airports: false,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    // 清除错误提示
    if (name === "multicity_code" || name === "airports") {
      setErrors({
        ...errors,
        [name]: false,
      });
    }
  };

  const handleSubmit = async () => {
    // 验证表单
    const newErrors = {
      multicity_code: !formData.multicity_code,
      multicity_code_text: "",
      airports: !formData.airports.length,
    };

    if (!formData.multicity_code) {
      newErrors.multicity_code_text = "Multi-city code is required";
    }

    if (formData.multicity_code.length < 3) {
      newErrors.multicity_code = true;
      newErrors.multicity_code_text = "Please enter at least 3 characters";
    }

    setErrors(newErrors);

    if (newErrors.multicity_code || newErrors.airports) {
      return;
    }
    try {
      setSubmitLoading(true);
      await setMultiCityCode({
        ...formData,
        airports: formData.airports.map((item) => item.iata),
      });
      await store?.getUserInfo?.();
      handleAddClose();
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (code: string) => {
    try {
      setDeleteLoading(code);
      await deleteMultiCityCode(code);
      await store?.getUserInfo?.();
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteLoading("");
    }
  };

  return (
    <>
      <Button
        variant="text"
        // size="small"
        color="primary"
        // variant="outlined"
        onClick={handleOpen}
        className={cn(className, "hover:bg-transparent !p-1 !leading-[12px] !text-[12px]")}
      >
        <AutoAwesomeIcon sx={{ fontSize: 15, color: "primary.main", mr: 0.3 }} />
        {buttonText}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={isMobile ? undefined : "md"}
        fullScreen={isMobile}
        aria-labelledby="supported-codes-dialog-title"
      >
        <DialogTitle id="supported-codes-dialog-title" sx={{ pb: 1 }}>
          <div className="flex items-center gap-2 text-primary">
            <AutoAwesomeIcon sx={{ fontSize: 20, color: "primary.main" }} />
            {title}
          </div>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" className="mb-[5px]">
            Multi-city codes are special shortcuts, like JAH, that represent a group of airports
            rather than just one. For example, JAH includes Japan’s major airports: HND, NRT, KIX,
            and ITM.
          </Typography>
          <Typography variant="body1" className="mb-[5px]">
            Selecting a multi-city code automatically adds all associated airports to your search,
            making it easy to check availability across multiple airports at once. You can also
            create your own custom codes for fast searches.
          </Typography>
          {/* <Typography variant="body1">
            When you select a multi-city code, all associated airports are automatically included in your search—making it easy to check availability across multiple airports at once. You can also create your own custom multi-city codes to quickly search multiple airports with a single click.
          </Typography> */}

          <div className="flex items-center mb-[15px] space-x-[10px] mt-[10px]">
            <Typography variant="h6" sx={{ mr: 0.5 }}>
              Custom Multi-City Codes
            </Typography>
            <IconButton
              size="small"
              color="primary"
              onClick={() => (openAdd ? handleAddClose() : handleAddOpen())}
              sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {openAdd ? (
                <CloseIcon sx={{ display: "flex", alignItems: "center" }} />
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  sx={{
                    minWidth: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AddIcon sx={{ display: "flex", alignItems: "center" }} /> Add Custom Code
                </Button>
              )}
            </IconButton>
          </div>

          {/* 添加多城市代码表单对话框 */}

          {openAdd && (
            <div className="flex flex-col space-y-4 mb-[20px] mt-[-15px]">
              <TextField
                label="Multi-City Code"
                name="multicity_code"
                value={formData.multicity_code.toUpperCase()}
                onChange={(e) => {
                  handleInputChange({
                    target: {
                      name: e.target.name,
                      value: e.target.value.toUpperCase(),
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                fullWidth
                required
                type="text"
                error={errors.multicity_code}
                helperText={errors.multicity_code ? errors.multicity_code_text : ""}
                margin="dense"
                size="small"
              />
              <TextField
                label="Description (Optional)"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                margin="dense"
                size="small"
                // helperText="Optional field"
              />

              <SelectAirport
                label="Airports"
                name="airports"
                value={formData.airports.join(",")}
                fullWidth
                required
                error={errors.airports}
                helperText={errors.airports ? "Airports is required" : ""}
                margin="normal"
                size="small"
                limitTags={6}
                maxCount={6}
                placeholder="Please enter the airport codes for your custom code."
                Icon={<FlightTakeoffOutlinedIcon />}
                selectData={formData.airports}
                onChange={(data) => {
                  setFormData({
                    ...formData,
                    airports: data,
                  });
                  setErrors({
                    ...errors,
                    airports: false,
                  });
                }}
              />

              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                fullWidth
                sx={{ mt: 2 }}
                loading={submitLoading}
                size="small"
              >
                Add Custom Multi-City Code
              </LoadingButton>
            </div>
          )}

          <CodesTable
            codes={customCodes}
            onDelete={handleDelete}
            deleteLoading={deleteLoading}
            showAction
          />

          <Typography variant="h6" className="!my-[10px]">
            Supported Multi-City Codes
          </Typography>
          <CodesTable codes={staticCodes} showAction={false} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupportedCodes;
